import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, Trash } from "@phosphor-icons/react";

export default function MangaRatings({ mangaId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ average: 0, count: 0, ratings: [] });
  const [myStars, setMyStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    const res = await api.get(`/manga/${mangaId}/ratings`);
    setData(res.data);
    if (user) {
      const mine = res.data.ratings.find(r => r.user_id === user.user_id);
      if (mine) { setMyStars(mine.stars); setComment(mine.comment || ""); }
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mangaId, user?.user_id]);

  async function submit(e) {
    e.preventDefault();
    if (!user) { toast.error("Entra para avaliar"); navigate("/login"); return; }
    if (myStars === 0) { toast.error("Escolhe pelo menos uma estrela"); return; }
    setSubmitting(true);
    try {
      await api.post(`/manga/${mangaId}/ratings`, { stars: myStars, comment });
      toast.success("Avaliação guardada");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  async function removeMine() {
    if (!confirm("Apagar a tua avaliação?")) return;
    try {
      await api.delete(`/manga/${mangaId}/ratings`);
      setMyStars(0);
      setComment("");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  return (
    <div className="mt-10" data-testid="manga-ratings">
      <div className="font-mono-accent text-xs text-zinc-500 mb-3 flex items-center gap-2">
        <Star size={16} weight="fill" className="text-[#F59E0B]" /> AVALIAÇÕES
      </div>

      {/* Average */}
      <div className="bg-[#121217] border border-white/5 rounded-2xl p-6 flex items-center gap-6 flex-wrap">
        <div className="text-center">
          <div className="font-display text-5xl font-extrabold text-[#F59E0B]">{data.average.toFixed(1)}</div>
          <div className="font-mono-accent text-xs text-zinc-500 mt-1">/ 10</div>
          <div className="font-mono-accent text-[10px] text-zinc-500 mt-1">{data.count} {data.count === 1 ? "AVALIAÇÃO" : "AVALIAÇÕES"}</div>
        </div>

        {/* User rating */}
        <form onSubmit={submit} className="flex-1 min-w-[280px]">
          <div className="font-mono-accent text-[10px] text-zinc-400 mb-2">{myStars > 0 ? "A TUA AVALIAÇÃO" : "AVALIA ESTE MANGÁ (1-10)"}</div>
          <div className="flex items-center gap-0.5 mb-3 flex-wrap" data-testid="rating-stars-picker">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(s => (
              <button
                key={s}
                type="button"
                onMouseEnter={() => setHover(s)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setMyStars(s)}
                data-testid={`rating-star-${s}`}
                className="p-0.5 hover:scale-110 transition-transform"
              >
                <Star size={22} weight={s <= (hover || myStars) ? "fill" : "regular"} className={s <= (hover || myStars) ? "text-[#F59E0B]" : "text-zinc-600"} />
              </button>
            ))}
            {(hover || myStars) > 0 && (
              <span className="ml-2 font-display font-bold text-[#F59E0B]">{hover || myStars}/10</span>
            )}
            {myStars > 0 && (
              <button type="button" onClick={removeMine} className="ml-3 text-zinc-500 hover:text-[#E63946]" title="Apagar avaliação" data-testid="rating-delete-btn">
                <Trash size={16} weight="bold" />
              </button>
            )}
          </div>
          <Textarea
            placeholder="Comentário (opcional)..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="bg-[#1C1C22] border-white/10 text-sm"
            data-testid="rating-comment-input"
          />
          <Button type="submit" disabled={submitting || myStars === 0} className="btn-crimson mt-3" data-testid="rating-submit-btn">
            {submitting ? "A guardar..." : "Guardar Avaliação"}
          </Button>
        </form>
      </div>

      {/* Comments list */}
      {data.ratings.length > 0 && (
        <div className="mt-6 space-y-3">
          {data.ratings.filter(r => r.comment).map(r => (
            <div key={r.rating_id} className="bg-[#121217] border border-white/5 rounded-xl p-4" data-testid={`rating-comment-${r.rating_id}`}>
              <div className="flex items-center gap-3 mb-2">
                <Link to={`/membros/${r.user_id}`} className="flex-shrink-0">
                  {r.author_picture ? (
                    <img src={resolveImageUrl(r.author_picture)} alt="" className="w-8 h-8 rounded-full object-cover hover:ring-2 hover:ring-[#E63946] transition-all" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-sm font-bold hover:ring-2 hover:ring-[#E63946] transition-all">{r.author_name?.[0]?.toUpperCase()}</div>
                  )}
                </Link>
                <div className="flex-1">
                  <Link to={`/membros/${r.user_id}`} className="font-display font-semibold text-sm hover:text-[#E63946] transition-colors" data-testid={`rating-author-link-${r.rating_id}`}>{r.author_name}</Link>
                  <div className="text-xs text-[#F59E0B] font-mono-accent">{r.stars}/10 ★</div>
                </div>
                <div className="font-mono-accent text-[10px] text-zinc-500">{new Date(r.created_at).toLocaleDateString("pt-PT")}</div>
                {user?.role === "admin" && (
                  <button
                    onClick={async () => {
                      if (!confirm("Apagar esta avaliação?")) return;
                      try { await api.delete(`/admin/ratings/${r.rating_id}`); toast.success("Avaliação apagada"); load(); }
                      catch (err) { toast.error(formatError(err.response?.data?.detail)); }
                    }}
                    className="text-zinc-500 hover:text-[#E63946] p-1"
                    title="Apagar (admin)"
                    data-testid={`admin-delete-rating-${r.rating_id}`}
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                )}
              </div>
              <p className="text-sm text-zinc-300 whitespace-pre-line">{r.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
